
// --- Vue & Template imports ---
import { Component, Vue, Watch} from 'vue-property-decorator';
import DownloadExcel from 'vue-json-excel/JsonExcel.vue';

// --- Models ---
import { Customer } from '@/models/Customer.model';
import { Route } from '@/models/Route.model';

// --- Services ---
import AppService from '@/services/app';
import CustomerService from '@/services/customer';
import ReportService from '@/services/report';
import RouteAjustmentService from '@/services/routeAdjustment';
import RouteService from '@/services/route';

// --- Third Party ---
import dayjs from 'dayjs';
import Hashids from 'hashids';

@Component({
  components: {   
    DownloadExcel,
  },
})
export default class RouteRateListing extends Vue {
  hashids = new Hashids(process.env.VUE_APP_HASHIDS_SALT);

  // used by Filter Area
  customerFilter: string = '';
  search: string = '';

  // used by No Data Slot
  isLoading: boolean = true;

  itemsPerPage: number = 50;
  routesDisplayedCount: number = 0;

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  title: string = '';
  clickedItem: Route = {
    id: 0,
    customerId: 0,
    customerName: '',
    customerCode: '',
    typeId: 0,
    type: '',
    from: '',
    to: '',
    rate: 0,
    routeKm: 0,
  };
  rateString: string = '';
  valid: boolean = false;
  clicked: boolean = false;

  // used by Dialog for Email Rates Sheet
  showEmailRatesSheetDialog: boolean = false;
  emailRatesSheetDialogTitle: string = '';
  emailValid: boolean = false;
  toEmailAddress: string = '';
  emailRatesClicked: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
    requiredNumber: (value: number) => value != null && value !== 0 || '',
    // renamed this here as another field is using requiredNumber validation and dont want to create a conflict
    requiredRateNumberCanBeZero: (value: number) => {
      if ((typeof value) === 'number') {
        return value.toString().length > 0 && value >= 0;
      } else {
        // will do requiredRateStringCanBeZero validation
        return true;
      }
    },
    requiredRateStringCanBeZero: (value: string) => {
      if ((typeof value) === 'string') {
        return !!value && parseFloat(value) >= 0 || '';
      } else {
        // will do requiredRateNumberCanBeZero validation
        return true;
      }
    },
    currency: (value: string) =>  /^\d{0,8}(?:\.\d{1,2})?$/.test(value) || 'Limited to two decimal places',
    maxRateValue: (value: number | string) => {
      if ((typeof value) === 'string') {
        return !!value && parseFloat('' + value) < 100000 || '';
      } else {
        return value < 100000 || '';
      }
    },
    maxAdjustmentValue: (value: number | string) => {
      if ((typeof value) === 'string') {
        return !!value && parseFloat('' + value) < 100000 || '';
      } else {
        return value < 100000 || '';
      }
    },
    email: (value: string) => /(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(value) || 'E-mail must be valid',
  };

  // used by Copy Dialog
  showCopyDialog: boolean = false;
  copyTitle: string = '';
  copyValid: boolean = false;
  copyCustomerId: number = 0;
  copyClicked: boolean = false;

  // used by Adjust Rates
  canAdjustRates: boolean = false;
  adjustRates: boolean = false;
  ratesToAdjust: Route[] = [];
  adjustRateValid: boolean = false;
  showAdjustRateDialog: boolean = false;
  adjustRateClicked: boolean = false;

  effectiveDatePicker: boolean = false;
  effectiveDate: string = dayjs().format('YYYY-MM-DD');
  adjustAmount: number = 0.00;
  adjustUp: number = 1;
  adjustmentDescription: string = '';

  // used by Confirm Action Dialog for Delete Item
  action: string = '';
  dialogContent: string = '';
  confirmActionDelay: boolean = false;
  showConfirmActionDialog: boolean = false;

  @Watch('adjustRates', {
    immediate: true,
    deep: true,
  })
  change() {
    if (!this.adjustRates) {
      this.ratesToAdjust = [];
    }
  }

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          sortable: false,
          text: 'Client',
          value: 'customerCode',
          filter: (value: string) => {
            if (this.customerFilter && this.customerFilter.length >= 1) {
              return (value === this.customerFilter);
            } else {
              return false;
            }
          },
        },
        {
          text: 'Rate Type',
          value: 'type',
        },
        {
          text: 'From',
          value: 'from',
        },
        {
          text: 'To',
          value: 'to',
        },
        {
          text: 'KM',
          value: 'routeKm',
        },
        {
          text: 'Rate',
          value: 'rate',
          align: 'end',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    async beforeMount() {
      await CustomerService.listAll(true);
      await RouteService.listRateTypes(false);
      await this.listAll();

      const customerId: string = this.hashids.decode(this.$route.params.id) + '';

      if (customerId && customerId.length > 0) {
        const thisCustomer = this.$store.getters['customers/customers'].find(
          (customer: Customer) => '' + customer.id === customerId,
        );
        if (thisCustomer !== undefined) {
          this.customerFilter = thisCustomer.code;
        }
      }
    }

    getToday() {
      return dayjs().format('YYYY-MM-DD');
    }

    async listAll() {
      try {
        this.isLoading = true;
        const response = await RouteService.listAll(true);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    updateItemCounter(e: any) {
      this.routesDisplayedCount = e.itemsLength;
    }

    getCustomerIdFromFilter() {
      if (this.customerFilter > '') {
        const thisCustomer = this.$store.getters['customers/customers'].find(
          (customer: Customer) => customer.code === this.customerFilter,

        );
        return thisCustomer.id;
      } else {
        return 0;
      }
    }

    selectCustomer() {
      this.adjustRates = false;

      const nonZeroRatedRates = this.$store.getters['routes/routes'].filter((item: Route) => item.customerId === this.getCustomerIdFromFilter() && item.rate > 0);
      this.canAdjustRates = (nonZeroRatedRates.length > 0);
    }

    allowEmailRatesFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 1},
        { type: 'Operator', level: 2},
      ]);
    }

    allowZeroRatesFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 3},
      ]);
    }

    allowCopyRatesFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 1},
      ]);
    }

    allowAdjustRatesFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
      ]);
    }
    
    allowExportFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 1},
        { type: 'Operator', level: 2},
      ]);
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 1},
        { type: 'Operator', level: 3},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 1},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 3},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 1},
        { type: 'Operator', level: 3},
      ]);
    }
  // #endregion
  
  // #region Functions required for the CSV Download
  getDataForCSV() {
    const selectedCustomerId = this.getCustomerIdFromFilter();
    const csvRouteRates = this.$store.getters['routes/routes'].filter((route: Route) => route.customerId === selectedCustomerId);
    csvRouteRates.sort((a: Route, b: Route) => (a.customerCode.toUpperCase() > b.customerCode.toUpperCase()) ? 1 : -1 );
    return csvRouteRates;
  }
  
  getFieldsForCSV() {
    return {
      'Client': 'customerCode',
      'Rate Type': 'type',
      'From': 'from',
      'To': 'to',
      'KM': 'routeKm',
      'Rate': 'rate',
    };
  }
  // #endregion

  // #region Functions required for the Item Dialog for Create/Update
    async launchNewItemDialog() {
      this.clickedItem = {
        id: 0,
        customerId: this.getCustomerIdFromFilter(),
        customerName: '',
        customerCode: '',
        typeId: 0,
        type: '',
        from: '',
        to: '',
        rate: 0,
        routeKm: 0,
      };

      this.rateString = this.clickedItem.rate.toFixed(2);

      this.valid = false;
      this.title = 'Create New Route Rate';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    launchUpdateItemDialog(item: Route) {
      this.clickedItem = Object.assign({}, item);
      this.clickedItem.routeKm = this.clickedItem.routeKm ? this.clickedItem.routeKm : 0; // fix for historically undefined / null values
      this.rateString = this.clickedItem.rate.toFixed(2);

      this.title = 'Update Route Rate';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await RouteService.create({
          customerId: this.clickedItem.customerId,
          typeId: this.clickedItem.typeId,
          from: this.clickedItem.from,
          to: this.clickedItem.to,
          rate: parseFloat(this.rateString),
          routeKm: this.clickedItem.routeKm ? this.clickedItem.routeKm : 0,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Routes.vue - doCreate - 1220 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;

        const response = await RouteService.update({
          id: this.clickedItem.id,
          typeId: this.clickedItem.typeId,
          from: this.clickedItem.from,
          to: this.clickedItem.to,
          rate: parseFloat(this.rateString),
          routeKm: this.clickedItem.routeKm ? this.clickedItem.routeKm : 0,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Routes.vue - doUpdate - 1259 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Dialog to Email Rates Sheet
    async launchEmailRatesSheetDialog() {
      if (this.customerFilter > '') {
        const thisCustomer = this.$store.getters['customers/customers'].find(
          (customer: Customer) => customer.code === this.customerFilter,

        );
        this.clickedItem.customerId = thisCustomer.id;
      } else {
        // should never happen
        this.clickedItem.customerId = 0;
      }

      this.emailValid = false;
      this.toEmailAddress = '';
      this.emailRatesSheetDialogTitle = 'Email ' + this.customerFilter + ' Rates Sheet';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showEmailRatesSheetDialog = true;
    }

    async emailRatesSheet() {
      try {
        this.emailRatesClicked = true;

        const response = await ReportService.emailRatesSheet({
          customerId: this.clickedItem.customerId,
          emailAddress: this.toEmailAddress,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            this.showEmailRatesSheetDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Routes.vue - emailRatesSheet - 1319 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.emailRatesClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Dialog to Copy Rates
    getCopyCustomers() {
      return this.$store.getters['customers/customers'].filter((item: Customer) => item.code !== this.customerFilter);
    }

    async launchCopyRatesDialog() {
      this.copyCustomerId = 0;
      this.copyValid = false;
      this.showCopyDialog = true;
    }

    async doCopyRates() {
      try {
        this.copyClicked = true;

        const response = await RouteService.copy({
          fromCustomerId: this.getCustomerIdFromFilter(),
          toCustomerId: this.copyCustomerId,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showCopyDialog = false;
            
            const thisCustomer = this.$store.getters['customers/customers'].find(
              (customer: Customer) => customer.id === this.copyCustomerId,
            );
            if (thisCustomer !== undefined) {
              this.customerFilter = thisCustomer.code;
            }
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Routes.vue - doCopyRates - 1373 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.copyClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Dialog to Adjust Rates
    async launchAdjustRatesDialog() {
      this.adjustRateValid = false;
      this.effectiveDate = dayjs().format('YYYY-MM-DD');
      this.adjustAmount = 0;
      this.adjustUp = 1;
      this.adjustmentDescription = this.customerFilter  + ' ' + dayjs().format('YYYY-MM-DD');

      this.showAdjustRateDialog = true;
    }

    async doAdjustment() {
       try {
        this.adjustRateClicked = true;

        const response = await RouteAjustmentService.create({
          customerId: this.getCustomerIdFromFilter(),
          routeIds: this.ratesToAdjust.filter((item: Route) => item.rate > 0).map((item: Route) => item.id).join(','),
          effectiveDate: this.effectiveDate,
          amount: this.adjustAmount,
          adjustUp: (this.adjustUp === 1),
          description: this.adjustmentDescription,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showAdjustRateDialog = false;
            this.adjustRates = false;
            this.itemsPerPage = 50;
            
            const thisCustomer = this.$store.getters['customers/customers'].find(
              (customer: Customer) => customer.id === this.copyCustomerId,
            );
            if (thisCustomer !== undefined) {
              this.customerFilter = thisCustomer.code;
            }
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Routes.vue - doAdjustment - 1433 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.adjustRateClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Delete/Zero Rates
    launchDeleteItemDialog(item: Route) {
      this.clickedItem = item;

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700; color: #C01212">delete</span> the '
        + item.type + ' Route from <span style="font-weight: 700">' 
        + item.from + ' to ' + item.to + '</span> for ' + item.customerCode + '.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    launchZeroRatesDialog() {

      this.action = 'ZERO';
      this.dialogContent = 'You are about to <span style="font-weight: 700; color: #00a4e3">update</span> all the Routes for ' 
        +  this.customerFilter + ' to <span style="font-weight: 700">ZERO</span>.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'DELETE') {
        await this.doDelete();
      } else if (this.action === 'ZERO') {
        await this.doZeroAllRates();
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;

        const response = await RouteService.delete({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Routes.vue - doDelete - 1501 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }

    async doZeroAllRates() {
      try {
        this.confirmActionDelay = true;

        const response = await RouteService.zeroAllRates({
          id: this.getCustomerIdFromFilter(),
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Routes.vue - doZeroAllRates - 1535 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion
}
